import {useState} from "react";
import Loading from "@/components/breadcrumbs/Loading";
import {defineMessage, FormattedMessage, useIntl} from "react-intl";
import {MenuItem, Select, Tooltip} from "@mui/material";

import {
    useHookClass,
    FormActionButtons,
    FormattedFormRow,
    FormContainer,
    FormattedTooltip,
} from "@dvrt/graphql-widgets";
import {FormattedPageTitle} from "@/components/pages";
import DataSource from "./dataSource";
import DomainBulkCapabilityForm from "./form";
import {CapabilityModificationType} from "@/api";
import {labelForCapabilitySet} from "@/utils/translations";

// Base component of the grant item
const GrantMenuItem = ({disabled}: {disabled: boolean}) => {
    return (
        <MenuItem value={CapabilityModificationType.Grant} disabled={disabled}>
            <FormattedMessage id="domain_settings.bulk_capability.grant" defaultMessage="Grant" />
        </MenuItem>
    );
};

// Add tooltip to the grant item if needed
const RenderGrantMenuitem = ({disabled}: {disabled: boolean}) => {
    if (disabled) {
        return (
            <FormattedTooltip
                title={defineMessage({
                    id: "domain_settings.bulk_capability.grant_disabled",
                    defaultMessage: "There are not enough capabilities available in this domain to grant for all users",
                })}
                placement="left"
            >
                <div>
                    <GrantMenuItem disabled={true} />
                </div>
            </FormattedTooltip>
        );
    }

    return <GrantMenuItem disabled={false} />;
};

const Content = ({domainId, dataSource}: {domainId: number; dataSource: DataSource}) => {
    const form = new DomainBulkCapabilityForm(domainId, dataSource);
    const [enabled, setEnabled] = useState(true);
    const intl = useIntl();

    async function submit() {
        setEnabled(false);
        await form.submit();
        setEnabled(true);
        form.reset();
    }

    return (
        <div>
            <div style={{marginBottom: "20px"}}>
                <FormattedPageTitle
                    desc={defineMessage({
                        id: "domain_settings.page_titles.bulk_capability",
                        defaultMessage: "Bulk capability modification for domain {domain}",
                    })}
                    values={{domain: dataSource.domainName}}
                />
                <FormContainer style={{width: 600}}>
                    {form.values.values.map((cap, index) => {
                        return (
                            <div style={{marginBottom: "0.5rem", display: "contents"}} key={index}>
                                <FormattedFormRow label={labelForCapabilitySet(cap.capability)}>
                                    <div style={{display: "flex", alignItems: "center", marginBottom: "1rem"}}>
                                        <Select
                                            variant="standard"
                                            value={cap.changeType}
                                            onChange={(e) =>
                                                form.setCapability(
                                                    cap.capability,
                                                    e.target.value as CapabilityModificationType,
                                                )
                                            }
                                        >
                                            <MenuItem value={CapabilityModificationType.Keep}>
                                                <FormattedMessage
                                                    id="domain_settings.bulk_capability.keep"
                                                    defaultMessage="Keep"
                                                />
                                            </MenuItem>
                                            <RenderGrantMenuitem disabled={!cap.allowGrant} />
                                            <MenuItem value={CapabilityModificationType.Revoke}>
                                                <FormattedMessage
                                                    id="domain_settings.bulk_capability.revoke"
                                                    defaultMessage="Revoke"
                                                />
                                            </MenuItem>
                                        </Select>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                marginLeft: "1rem",
                                                cursor: "default",
                                            }}
                                        >
                                            <Tooltip title={intl.formatMessage({id: "used", defaultMessage: "Used"})}>
                                                <div>{cap.used}</div>
                                            </Tooltip>
                                            <span style={{margin: "0 2px"}}> / </span>
                                            <Tooltip
                                                title={intl.formatMessage({
                                                    id: "allocated",
                                                    defaultMessage: "Allocated",
                                                })}
                                            >
                                                <div>{cap.allocated}</div>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </FormattedFormRow>
                            </div>
                        );
                    })}
                    <FormattedFormRow>
                        <div style={{marginTop: "1rem"}}>
                            <FormActionButtons
                                mainAction={submit}
                                secondaryAction={() => form.reset()}
                                enabled={enabled && form.hasChanged}
                            />
                        </div>
                    </FormattedFormRow>
                </FormContainer>
            </div>
        </div>
    );
};

export const DomainBulkCapability = ({domainId}: {domainId: number}) => {
    const dataSource = useHookClass(DataSource, domainId);

    if (!dataSource.loaded) {
        return <Loading />;
    }

    return <Content domainId={domainId} dataSource={dataSource} />;
};
